import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import type { Boat } from '@todo/core/boats';
import { boatPortPretty } from '@todo/core/boats';
import { localizedBoatType } from '@/lib/i18n/boat-content';
import { currencySymbol } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';

// Labels for the specs.skipper values.
const SKIPPER_KEY = {
  included: 'boats.facts.skipperIncluded',
  optional: 'boats.facts.skipperValue',
  not_available: 'boats.facts.skipperNotAvailable',
} as const;

// Per-boat facts list, rendered straight from columns plus the specs
// describe-boats extracted from the owner's listing text (numbers it checked
// against that text) — unique per boat, keyword-rich, zero fluff. Shared by
// the native and web detail screens. See "Structured depth" and "Owner text
// and indexing" in docs/click-and-boat-integration.md.
export function BoatFacts({ boat, locale }: { boat: Boat; locale: Locale }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const port = boatPortPretty(boat.booking_url);
  const ownerLabel = boat.owner_type
    ? t(`boats.ownerLabel.${boat.owner_type}`, { defaultValue: boat.owner_type })
    : null;
  const priceLabel =
    boat.from_price != null
      ? `${currencySymbol(boat.currency)}${boat.from_price}`
      : null;

  const specs = boat.translations?.specs;
  const number = (n: number) => new Intl.NumberFormat(locale).format(n);
  const count = (key: string, n: number | null | undefined) =>
    n != null ? [{ key, label: t(`boats.facts.${key}`), value: number(n) }] : [];
  const equipment = (specs?.equipment ?? []).map((code) =>
    t(`boats.equipment.${code}`, { defaultValue: '' }),
  ).filter(Boolean);

  const rows: { key: string; label: string; value: string }[] = [
    { key: 'type', label: t('boats.facts.type'), value: localizedBoatType(boat, locale) },
    ...(port ? [{ key: 'port', label: t('boats.facts.port'), value: port }] : []),
    ...(priceLabel ? [{ key: 'price', label: t('boats.facts.price'), value: priceLabel }] : []),
    ...(boat.rating != null
      ? [{ key: 'rating', label: t('boats.facts.rating'), value: `${boat.rating.toFixed(1)} / 5` }]
      : []),
    ...(boat.year != null
      ? [{ key: 'year', label: t('boats.facts.year'), value: String(boat.year) }]
      : []),
    ...(ownerLabel ? [{ key: 'owner', label: t('boats.facts.owner'), value: ownerLabel }] : []),
    ...(specs?.passengers != null
      ? [{ key: 'passengers', label: t('boats.facts.passengers'), value: t('boats.facts.passengersValue', { value: number(specs.passengers) }) }]
      : []),
    ...(specs?.length_m != null
      ? [{ key: 'length', label: t('boats.facts.length'), value: t('boats.facts.lengthValue', { value: number(specs.length_m) }) }]
      : []),
    ...count('cabins', specs?.cabins),
    ...count('berths', specs?.berths),
    ...count('bathrooms', specs?.bathrooms),
    ...(specs?.engine_hp != null
      ? [{ key: 'engine', label: t('boats.facts.engine'), value: t('boats.facts.engineValue', { value: number(specs.engine_hp) }) }]
      : []),
    ...(specs?.licence_required != null
      ? [{
          key: 'licence',
          label: t('boats.facts.licence'),
          value: t(specs.licence_required ? 'boats.facts.licenceRequired' : 'boats.facts.licenceNotRequired'),
        }]
      : []),
    { key: 'basis', label: t('boats.facts.rentalBasis'), value: t('boats.facts.rentalBasisValue') },
    {
      key: 'skipper',
      label: t('boats.facts.skipper'),
      value: t(specs?.skipper ? SKIPPER_KEY[specs.skipper] : 'boats.facts.skipperValue'),
    },
  ];

  return (
    <View style={styles.root}>
      <Text
        role="heading"
        aria-level={2}
        style={[styles.heading, { color: theme.text }]}>
        {t('boats.facts.heading')}
      </Text>
      {rows.map((row) => (
        <View key={row.key} style={[styles.row, { borderColor: theme.border }]}>
          <Text style={[styles.label, { color: theme.mutedForeground }]}>{row.label}</Text>
          <Text style={[styles.value, { color: theme.text }]}>{row.value}</Text>
        </View>
      ))}
      {equipment.length > 0 ? (
        <>
          <Text
            role="heading"
            aria-level={3}
            style={[styles.heading, styles.subheading, { color: theme.text }]}>
            {t('boats.facts.onBoard')}
          </Text>
          <View style={styles.chips}>
            {equipment.map((label) => (
              <Text
                key={label}
                style={[styles.chip, { color: theme.text, borderColor: theme.border }]}>
                {label}
              </Text>
            ))}
          </View>
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: 2,
  },
  heading: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  label: {
    fontSize: 13,
  },
  value: {
    fontSize: 13,
    fontWeight: '600',
  },
  subheading: {
    marginTop: Spacing.three,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    fontSize: 13,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 999,
  },
});
